import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Card,
  CardContent,
  Chip,
  ChipDelete,
  Dropdown,
  IconButton,
  ListItemDecorator,
  Menu,
  MenuButton,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/joy';
import {
  ArrowsDownUp,
  CaretDown,
  CaretUp,
  DotsSixVertical,
  DotsThree,
  PencilSimpleLine,
  Trash,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import {
  AttributeColorSwatch,
  getAttributeValueLabel,
} from 'companySettings/components/PartFinderAttributeValues';
import { AttributeType } from 'companySettings/enums';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

import { IProductTypeAttributeField } from './types';

interface ICardProps {
  attribute: IProductTypeAttributeField;
  isFirst: boolean;
  isLast: boolean;
  onEdit: () => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDefineValuesOrder: () => void;
  onRemoveValue: (valueIndex: number) => void;
}

// One attribute of the product type: its name, its values as removable chips and the actions
// on it. Dragging the handle reorders the attributes.
const AttributeCard = ({
  attribute,
  isFirst,
  isLast,
  onEdit,
  onRemove,
  onMoveUp,
  onMoveDown,
  onDefineValuesOrder,
  onRemoveValue,
}: ICardProps) => {
  const { t } = useTranslation();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: attribute.id,
  });

  return (
    <Card
      ref={setNodeRef}
      variant={Variant.OUTLINED}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <CardContent>
        <Stack sx={{ gap: 2 }}>
          <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1 }}>
            <Box
              {...attributes}
              {...listeners}
              sx={{ display: 'flex', color: 'neutral.500', cursor: 'grab', touchAction: 'none' }}
            >
              <DotsSixVertical weight="bold" />
            </Box>

            <Typography level={Level.TITLE_MD} sx={{ flexGrow: 1 }}>
              {attribute.name}
            </Typography>

            <Dropdown>
              <Tooltip title={t('labelMoreActions')}>
                {/* The theme gives every menu button a caret; this one is just the ⋯ icon. */}
                <MenuButton
                  endDecorator={null}
                  slots={{ root: IconButton }}
                  slotProps={{
                    root: { variant: Variant.SOFT, color: Color.NEUTRAL, size: Size.SM },
                  }}
                >
                  <DotsThree weight="bold" />
                </MenuButton>
              </Tooltip>
              {/* Above the drawer the card sits in: menus default to a layer below modals. */}
              <Menu placement="bottom-end" sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
                <MenuItem onClick={onDefineValuesOrder} disabled={attribute.values.length < 2}>
                  <ListItemDecorator>
                    <ArrowsDownUp />
                  </ListItemDecorator>
                  {t('buttonLabelDefineValuesOrder')}
                </MenuItem>
                <MenuItem onClick={onMoveUp} disabled={isFirst}>
                  <ListItemDecorator>
                    <CaretUp />
                  </ListItemDecorator>
                  {t('buttonLabelMoveUp')}
                </MenuItem>
                <MenuItem onClick={onMoveDown} disabled={isLast}>
                  <ListItemDecorator>
                    <CaretDown />
                  </ListItemDecorator>
                  {t('buttonLabelMoveDown')}
                </MenuItem>
                <MenuItem onClick={onEdit}>
                  <ListItemDecorator>
                    <PencilSimpleLine />
                  </ListItemDecorator>
                  {t('buttonLabelEdit')}
                </MenuItem>
                <MenuItem onClick={onRemove} color={Color.DANGER}>
                  <ListItemDecorator>
                    <Trash />
                  </ListItemDecorator>
                  {t('buttonLabelRemove')}
                </MenuItem>
              </Menu>
            </Dropdown>
          </Stack>

          <Stack direction={Direction.ROW} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {attribute.values.map((value, valueIndex) => (
              <Chip
                key={`${attribute.id}-${valueIndex}`}
                variant={Variant.OUTLINED}
                color={Color.NEUTRAL}
                startDecorator={
                  AttributeType.COLOUR === attribute.type && (
                    <AttributeColorSwatch colorHex={value.colorHex} />
                  )
                }
                endDecorator={<ChipDelete onDelete={() => onRemoveValue(valueIndex)} />}
              >
                {getAttributeValueLabel(attribute.type, value, t)}
              </Chip>
            ))}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

interface IProps {
  attributes: IProductTypeAttributeField[];
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
  onDefineValuesOrder: (index: number) => void;
  onRemoveValue: (index: number, valueIndex: number) => void;
}

export const AttributeCards = ({
  attributes,
  onEdit,
  onRemove,
  onMove,
  onDefineValuesOrder,
  onRemoveValue,
}: IProps) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    onMove(
      attributes.findIndex((attribute) => attribute.id === active.id),
      attributes.findIndex((attribute) => attribute.id === over.id)
    );
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext
        items={attributes.map((attribute) => attribute.id)}
        strategy={verticalListSortingStrategy}
      >
        <Stack sx={{ gap: 2 }}>
          {attributes.map((attribute, index) => (
            <AttributeCard
              key={attribute.id}
              attribute={attribute}
              isFirst={0 === index}
              isLast={attributes.length - 1 === index}
              onEdit={() => onEdit(index)}
              onRemove={() => onRemove(index)}
              onMoveUp={() => onMove(index, index - 1)}
              onMoveDown={() => onMove(index, index + 1)}
              onDefineValuesOrder={() => onDefineValuesOrder(index)}
              onRemoveValue={(valueIndex) => onRemoveValue(index, valueIndex)}
            />
          ))}
        </Stack>
      </SortableContext>
    </DndContext>
  );
};
